import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Sigma, Loader2 } from "lucide-react";
import api from "../lib/api";
import { MathText } from "../components/MathText";
import { useT } from "../context/LanguageContext";
import TopBar from "../components/TopBar";

export default function Formulas() {
  const { examId, topicId } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/exams/${examId}/formulas/${topicId}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [examId, topicId]);

  if (err) return (
    <div className="space-bg min-h-screen flex flex-col items-center justify-center px-5 text-center">
      <p className="text-white/70 mb-4">{t("Errore nel caricare il formulario. Riprova.")}</p>
      <button onClick={() => navigate(`/exam/${examId}`)} className="px-6 py-3 rounded-2xl btn-neon font-heading font-bold bouncy" data-testid="formulas-err-back">{t("Indietro")}</button>
    </div>
  );

  if (!data) return (
    <div className="space-bg min-h-screen flex flex-col items-center justify-center gap-3 text-white/70">
      <Loader2 size={36} className="animate-spin" style={{ color: "var(--gold)" }} />
      {t("L'AI sta raccogliendo tutte le formule...")}
    </div>
  );

  return (
    <div className="space-bg min-h-screen pb-20">
      <TopBar />
      <div className="max-w-2xl mx-auto px-4 pt-5">
        <button onClick={() => navigate(`/exam/${examId}`)} className="flex items-center gap-1 text-white/60 bouncy mb-4" data-testid="formulas-back-btn">
          <ChevronLeft size={18} /> {t("Esame")}
        </button>
        <h1 className="font-heading font-black text-3xl tracking-tight flex items-center gap-2 mb-2">
          <Sigma style={{ color: "var(--gold)" }} /> {t("Formulario")}
        </h1>
        <p className="text-white/50 text-sm mb-6">{data.topic_name} · {t("tutte le formule da ricordare")}</p>

        {data.groups.length === 0 ? (
          <p className="text-white/50 text-center py-8" data-testid="formulas-empty">{t("Nessuna formula disponibile.")}</p>
        ) : (
          <div className="space-y-6">
            {data.groups.map((g, gi) => (
              <div key={gi} data-testid={`formula-group-${gi}`}>
                <h2 className="font-heading font-bold text-lg tracking-tight mb-3" style={{ color: "var(--gold)" }}>{g.topic}</h2>
                <div className="space-y-3">
                  {(g.formulas || []).map((f, fi) => (
                    <motion.div key={fi} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: fi * 0.03 }}
                      className="glass rounded-2xl p-4" data-testid={`formula-${gi}-${fi}`}>
                      <h3 className="font-heading font-bold text-sm mb-2">{f.name}</h3>
                      <div className="bg-black/30 rounded-xl px-4 py-3 my-2 overflow-x-auto text-center">
                        <MathText>{f.formula}</MathText>
                      </div>
                      {f.symbols && <p className="text-xs text-white/60 mt-2"><span className="text-white/40">{t("Simboli: ")}</span><MathText>{f.symbols}</MathText></p>}
                      {f.usage && <p className="text-xs text-white/60 mt-1"><span className="text-white/40">{t("Quando si usa: ")}</span><MathText>{f.usage}</MathText></p>}
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
